import React from 'react';
import { CSSProp } from '../types';
import { styled } from '../theming';
import responsiveProp, {
  CSSValue,
  ResponsiveProp,
  responsivePropType,
} from '../utils/responsiveProp';
import PropTypes from 'prop-types';
import { getPadding } from './Layout.styles';
import type {
  PolymorphicComponentPropsWithRef,
  PolymorphicForwardRefComponent,
  PolymorphicRef,
} from '../utils/types';

export interface BoxInternalProps {
  children?: React.ReactNode;
  padding?: ResponsiveProp<CSSValue>;
  backgroundColor?: CSSProp<string>;
}

export type BoxProps<
  C extends 'div' | 'section' | 'header' | 'article' | 'aside' | 'main' = 'div'
> = PolymorphicComponentPropsWithRef<C, BoxInternalProps>;

export type BoxComponent<
  C extends 'div' | 'section' | 'header' | 'article' | 'aside' | 'main' = 'div'
> = PolymorphicForwardRefComponent<
  'div' | 'section' | 'header' | 'article' | 'aside' | 'main',
  C,
  BoxInternalProps
>;

const StyledBox = styled.div<BoxInternalProps>(({ padding = 0, backgroundColor }) => ({
  backgroundColor,
  ...responsiveProp(padding, (padding) => ({ padding: getPadding(padding) })),
}));

function _Box<C extends 'div' | 'section' | 'header' | 'article' | 'aside' | 'main' = 'div'>(
  props: BoxProps<C>,
  ref?: PolymorphicRef<C>
) {
  const { children, padding = 0, backgroundColor, ...otherProps } = props;

  const commonProps = {
    children,
    padding,
    backgroundColor,
    ...otherProps,
  };

  return (
    <StyledBox {...commonProps} ref={ref}>
      {children}
    </StyledBox>
  );
}

export const Box = React.forwardRef(_Box) as BoxComponent;

Box.propTypes = {
  backgroundColor: PropTypes.string,
  padding: responsivePropType(
    PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    ])
  ) as PropTypes.Validator<ResponsiveProp<CSSValue>>,
};
